/**
 * Clarity Shopping Agent — remember the customer's dismissal for 24h.
 * ─────────────────────────────────────────────────────────────────────────────
 * The app re-renders its widget on every page load, so a customer who closes it
 * has to dismiss it again on the next page. Here we watch for the agent's own
 * Dismiss button and, once clicked, suppress the widget for MB_AGENT_TTL.
 * (Note: the panel's "Close" button only collapses the chat back to the pill —
 * "Dismiss" is the one that means "go away", so only that one counts here.)
 *
 * The widget lives in an OPEN shadow root under #ads-agent-host, so we can
 * delegate a click listener onto the root. The host itself is a normal
 * light-DOM element, which is what we hide.
 *
 * Timing: the app injects the host asynchronously. When a dismissal is still
 * active we install the hiding rule immediately (before the host exists) so the
 * widget never flashes; otherwise we wait for the host via MutationObserver to
 * attach the Close listener.
 */

const MB_AGENT_KEY = 'mb-agent-dismissed-at';
const MB_AGENT_TTL = 24 * 60 * 60 * 1000; // 1 day
const MB_AGENT_HOST_ID = 'ads-agent-host';

// localStorage throws in some privacy modes — never let that break the page.
const mbAgentReadDismissedAt = () => {
  try {
    return parseInt(localStorage.getItem(MB_AGENT_KEY) || '0', 10) || 0;
  } catch {
    return 0;
  }
};

const mbAgentRecordDismissal = () => {
  try {
    localStorage.setItem(MB_AGENT_KEY, String(Date.now()));
  } catch {
    /* ignore — worst case the widget shows again next page */
  }
};

const mbAgentIsDismissed = () => {
  const at = mbAgentReadDismissedAt();
  return at > 0 && Date.now() - at < MB_AGENT_TTL;
};

if (mbAgentIsDismissed()) {
  const style = document.createElement('style');
  style.textContent = `#${MB_AGENT_HOST_ID} { display: none !important; }`;
  document.head.appendChild(style);
} else {
  const wireCloseListener = () => {
    const root = document.getElementById(MB_AGENT_HOST_ID)?.shadowRoot;
    if (!root || root.mbAgentWired) return false;
    root.mbAgentWired = true;

    // Capture phase so we still see the click even though the app stops
    // propagation on its own handler. We only observe — the app keeps
    // handling the actual close.
    root.addEventListener(
      'click',
      (event) => {
        if (!event.target.closest?.('[aria-label="Dismiss"]')) return;
        mbAgentRecordDismissal();
        const host = document.getElementById(MB_AGENT_HOST_ID);
        if (host) host.style.display = 'none';
      },
      true
    );
    return true;
  };

  if (!wireCloseListener()) {
    const observer = new MutationObserver(() => {
      if (wireCloseListener()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    // Safety valve — stop watching if the app never loads.
    setTimeout(() => observer.disconnect(), 30000);
  }
}
